import { PlayerProjectionFragment } from '@/api/events/query.generated';
import { EventInfo } from '@/api/events/types/types';
import { queryClient } from '@/data';
import { authUserKeys } from '@/hooks/use-auth-user-config';
import { UserSettingsResponse } from '@/hooks/use-auth-user-settings';
import { MarketStatus } from '@/types/api.generated';
import { logger } from '@/utils/logging';
import { runStoreUpdate } from '@/utils/zustand';
import { create } from 'zustand';

import { LineupUpdateContextType } from '../components/LineupUpdateModalProvider';
import { PlayerProjection } from '../types';
import { getPicksWithChangeStatus } from '../utils/betslip-projection-changed';
import { eventUtils } from '../utils/event-utils';
import { createPlayerEventKey } from '../utils/markets-cache-store-utils';
import { arePlayerProjectionsEqual } from '../utils/projections-equal';
import { useBetslipStore } from './use-betslip-store';

interface PlayerMarketsStore {
    _playerMarkets: Record<string, PlayerProjection[]>;
    actions: {
        updateStorePlayerProjections: (playerId: string, eventId: string, projections: PlayerProjection[]) => void;
        updateStorePlayersProjections: (markets: Record<string, PlayerProjection[]>) => void;
        /**
         * Used for debugging purposes, to suspend markets for a given player ID.
         * @param playerId - the player ID for which to simulate suspended markets
         * @param eventId - the event ID for which to simulate suspended markets
         * @param mode - if the method should suspend all markets, or leave one active
         */
        _debugChangePlayerMarketsStatus: (
            playerId: string,
            eventId: string,
            mode: 'all' | 'leave_one',
            marketStatus: MarketStatus
        ) => void;
        /**
         * Used for debugging purposes, simulates 'currentValues' for a given player ID.
         * @param playerId - the player ID for which to simulate 'currentValues'
         * @param eventId - the event ID for which to simulate 'currentValues'
         */
        _debugEnableCurrentValues: (playerId: string, eventId: string) => void;
    };
}

/**
 * Store that caches all the player projections that are loaded.
 * All components that need to access the player projections should use this store.
 * All components that fetch player projections should also update this store.
 */
export const usePlayerPropsStore = create<PlayerMarketsStore>()((set, get) => {
    return {
        _playerMarkets: {},
        actions: {
            updateStorePlayerProjections: (playerId, eventId, markets) => {
                const key = createPlayerEventKey(playerId, eventId);
                const playerMarkets = get()._playerMarkets;
                if (arePlayerProjectionsEqual(playerMarkets[key], markets)) {
                    return;
                }
                set({ _playerMarkets: { ...playerMarkets, [key]: markets } });
            },
            updateStorePlayersProjections: markets => {
                const currentMarkets = get()._playerMarkets;
                // Only replace markets that actually changed to preserve reference stability
                // so that useShallow subscribers for unchanged players don't re-render
                const updatedMarkets: Record<string, PlayerProjection[]> = {};
                let hasChanges = false;
                for (const key of Object.keys(markets)) {
                    if (!arePlayerProjectionsEqual(currentMarkets[key], markets[key])) {
                        updatedMarkets[key] = markets[key];
                        hasChanges = true;
                    }
                }
                if (hasChanges) {
                    set({ _playerMarkets: { ...currentMarkets, ...updatedMarkets } });
                }
            },
            _debugChangePlayerMarketsStatus: (
                playerId: string,
                eventId: string,
                mode: 'all' | 'leave_one',
                marketStatus: MarketStatus
            ) => {
                const key = createPlayerEventKey(playerId, eventId);
                const marketsForPlayer = get()._playerMarkets[key];
                if (marketsForPlayer && marketsForPlayer.length > 0) {
                    const firstMarketToChange = mode === 'all' ? 0 : 1;
                    const updatedMarkets = marketsForPlayer.map((market, i) =>
                        i >= firstMarketToChange ? { ...market, marketStatus } : market
                    );
                    set({ _playerMarkets: { ...get()._playerMarkets, [key]: updatedMarkets } });
                } else {
                    logger.debug(`No player markets found for ${playerId}`);
                }
            },
            _debugEnableCurrentValues: (playerId: string, eventId: string) => {
                const key = createPlayerEventKey(playerId, eventId);
                const marketsForPlayer = get()._playerMarkets[key];
                if (marketsForPlayer && marketsForPlayer.length > 0) {
                    const updatedMarkets = marketsForPlayer.map(market => ({
                        ...market,
                        currentValue: market.value / 2,
                    }));
                    set({ _playerMarkets: { ...get()._playerMarkets, [key]: updatedMarkets } });
                } else {
                    logger.debug(`No player markets found for ${playerId}`);
                }
            },
        },
    };
});

/**
 * Selects the OPENED markets for the given playerId
 */
export const playerPropsSelector = (playerId: string, eventId: string) => (state: PlayerMarketsStore) =>
    state._playerMarkets[createPlayerEventKey(playerId, eventId)]?.filter(
        it => it.marketStatus === MarketStatus.Opened
    ) ?? [];

/**
 * Selects all the markets for the given playerId
 */
const NO_PLAYER_MARKETS: PlayerProjectionFragment[] = [];
export const allPlayerPropsSelector = (playerId: string, eventId: string) => (state: PlayerMarketsStore) =>
    state._playerMarkets[createPlayerEventKey(playerId, eventId)] ?? NO_PLAYER_MARKETS;

class PlayerPropsStoreUpdater {
    LOG_TAG = 'PlayerPropsStoreUpdater';
    private modalContext: LineupUpdateContextType | null = null;

    public setModalContext = (modalContext: LineupUpdateContextType | null) => {
        this.modalContext = modalContext;
    };
    public updatePlayerStoreWithNewData = (data: EventInfo[] | undefined, options: { debugTag?: string }) => {
        const updatePlayersMarkets = usePlayerPropsStore.getState().actions.updateStorePlayersProjections;
        const updateBetslipProjections = useBetslipStore.getState().actions.updateBetslipProjections;

        if (data && data.length > 0) {
            const currentPicks = useBetslipStore.getState().betslip ?? [];
            const playersMap = eventUtils.getAllPlayersAsMap(data);
            runStoreUpdate(() => updatePlayersMarkets(playersMap));

            const userData = queryClient.getQueryData<UserSettingsResponse>(authUserKeys.settings());

            const acceptAllProjectionsChanges = userData && userData.data.accept_all_odds_changes;

            const picksWithChangedProjections = getPicksWithChangeStatus({
                picks: currentPicks,
                events: data,
                //Projection 'type-change' eg: Regular->Edge, is handled as value changes
                //If user auto-accepts projection changes, we updated them, otherwise show the modal
                statuses: ['value-changed', 'type-changed'],
            });
            // Also update the market status of betslip picks that might got suspended / opened during LIVE games
            const picksWithChangedMarketStatus = getPicksWithChangeStatus({
                picks: currentPicks,
                events: data,
                statuses: ['resumed', 'suspended'],
            });

            const picksWithChangedMarketIDs = getPicksWithChangeStatus({
                picks: currentPicks,
                events: data,
                statuses: ['market-id-change'],
            });

            if (picksWithChangedMarketIDs.length > 0) {
                const players = picksWithChangedMarketIDs
                    .map(pick => `${pick.pick.player.firstName} ${pick.pick.player.lastName}`)
                    .join(', ');
                logger.debug(
                    this.LOG_TAG,
                    'FetchAPIData - Market ID changed for some picks, updating them locally',
                    players
                );
                updateBetslipProjections([...picksWithChangedMarketIDs]);
            }

            if (acceptAllProjectionsChanges) {
                runStoreUpdate(() =>
                    updateBetslipProjections([
                        ...picksWithChangedProjections,
                        ...picksWithChangedMarketStatus,
                        ...picksWithChangedMarketIDs,
                    ])
                );
            } else {
                runStoreUpdate(() =>
                    updateBetslipProjections([...picksWithChangedMarketIDs, ...picksWithChangedMarketStatus])
                );
                if (picksWithChangedProjections.length > 0) {
                    logger.debug(this.LOG_TAG, 'showLineupUpdateModal', options.debugTag, picksWithChangedProjections);
                    if (!this.modalContext) {
                        logger.warn(
                            this.LOG_TAG,
                            'showLineupUpdateModal - modalContext is null, cannot show the modal',
                            options.debugTag
                        );
                    } else {
                        this.modalContext.showLineupUpdateModal({ picksWithChangedProjections });
                    }
                }
            }
        }
    };
}

export const PicksPlayerPropsStoreUpdater = new PlayerPropsStoreUpdater();
